import React, { useCallback, useEffect, useRef, useState } from 'react'
import { apiDelete, apiDownloadFile, apiGet, apiPost, apiPut, apiFile } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { IconButton, CircularProgress, Tooltip, Box, Typography } from '@mui/material'
import { FileDownloadOutlined, Star, StarBorder, Visibility } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import AlertUI from '../../generic/AlertUI'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import DocumentsCarouselView from './DocumentsCarouselView'
// From the import-free leaf module — importing it from SmartDocumentViewer would drag
// the heavy 2D/3D viewers (three.js) into every consumer of Document (monitoring cards).
import { isExtensionViewable } from './documentExtensions'
import { isPreviewableImage, isAllowedMainImage, mainImageAllowedExtensions } from '../parts/MainImageThumbnail'
import { mainImageResource } from './mainImageResource'
import { buildAppendDocumentPayloads, partitionUploadFiles } from './documentBatchUpload'
import { buildDocumentCreateUrl } from './documentApiUrls'
import { useToggle } from '../../generic/hooks/useToggle'
import { useProtectedRowPlaceholder, useVisionCoversSensitive } from '../../vision/protectedRow'

// Import typedefs from another file
/**
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
 */

export const validateDocumentRow = (row, existingList, t_) => {
  if (row?.fileName && row.fileName.length > 80) {
    return { errorFields: ['fileName'], alertMessage: t_('File name cannot exceed 80 characters') }
  }
  const duplicate = existingList.find(doc => doc.documentName === row.documentName && doc.fileName === row.fileName && doc.id !== row.id)
  if (duplicate) {
    return { errorFields: ['documentName', 'fileName'], alertMessage: t_('Document with the same name and file already exists') }
  }
}

/**
 * Document object
 * @param {Object} props Props object
 * @param {IdCodeName} props.reference Reference
 * @param {'PART'|'PART_REFERENCE'|'FEATURE_TASK'|'TOOL_REFERENCE'|'TOOL'|'FABRICATION_ORDER'|'DEVICE'|'WORKFLOW'|'JOB'|'TASK'|'DEVICE_GROUP'|
 * 'RAW_MATERIAL_REFERENCE'|'RAW_MATERIAL'|'FIXTURE_REFERENCE'|'FIXTURE'|'FIXTURE_ASSEMBLY_REFERENCE'|'FIXTURE_ASSEMBLY'|'FEATURE_GROUP'|'ORDER'|'ORDER_LINE'|
 * 'FEATURE_PART_REFERENCE'|'DELIVERY_NOTE'|'PURCHASE_ORDER_THEORETICAL_RECEPTION'|'PURCHASE_ORDER_REAL_RECEPTION'|'PURCHASE_ORDER_QUALITATIVE_RECEPTION'|
 * 'MATERIAL_RECEPTION_INCIDENCE'|'QUALITY_INCIDENCE'|'QUALITY_INCIDENCE_ACTION'|'INSPECTION_REPORT_FEATURE'} props.resourceType Resource type
 * @param {string} props.permissionResource
 * @param {boolean} props.isDownloadingFile If true, a file is downloading
 * @param {React.Dispatch<SetStateAction<boolean>>} props.setIsDownloadingFile Use state to set if the file is downloading or not
 * @param {string} props.referenceTempUUID temporal uuid to create or update reference
 * @param {string} props.referenceUUID uuid to create or update reference
 * @param {boolean} props.enableFileDrop If true, dropping files on the window creates one document
 * per file automatically (default name, default type and appended order), same as the multi-file
 * drop of the task instruction documents header
 * @returns {JSX.Element} Document component
 */
const Document = ({ reference, resourceType, permissionResource, isDownloadingFile, setIsDownloadingFile, referenceTempUUID, referenceUUID, viewMode = false, defaultName, onMainImageChange = () => {}, enableFileDrop = false }) => {
  const { t_ } = useSmartTranslation()
  // Story 213 S302 (C15.6, PO C15-5): while it covers, the WHOLE row of a protected document is the blurred placeholder
  // (its file name gave it away), and "select all the data" is off (DK-2).
  const protectedRowPlaceholder = useProtectedRowPlaceholder()
  const visionCoversSensitive = useVisionCoversSensitive()
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false)
  /** @type {[import('../../generic/utilities/objectStructures/documentWindow/document').Document, React.Dispatch<SetStateAction<import('../../generic/utilities/objectStructures/documentWindow/document').Document>>]} */
  const [selectedDocument, setSelectedDocument] = useState({})
  const [AlertElement, showAlert] = AlertUI()
  /** @type {[string[], React.Dispatch<SetStateAction<string[]>>]} */
  const [downloadingRowId, setDownloadingRowId] = useState(null)
  /** @type {[Document, React.Dispatch<SetStateAction<Document>>]} */
  const documentListRef = useRef([])
  const isUserAuthorized = useAuthorization()
  const frontEndConfig = useFrontEndConfig()[0]

  const isUserAuthorizedForAction = useCallback((action) => {
    const permissionEnabledResourceTypes = ['JOB', 'WORKFLOW', 'PART', 'PART_REFERENCE', 'TOOL', 'TOOL_REFERENCE', 'FIXTURE_REFERENCE', 'FIXTURE', 'FIXTURE_ASSEMBLY_REFERENCE', 'FIXTURE_ASSEMBLY', 'RAW_MATERIAL', 'RAW_MATERIAL_REFERENCE', 'DEVICE', 'FABRICATION_ORDER', 'FEATURE_TASK', 'FEATURE_GROUP', 'ORDER', 'ORDER_LINE', 'DELIVERY_NOTE', 'PURCHASE_ORDER_THEORETICAL_RECEPTION', 'PURCHASE_ORDER_REAL_RECEPTION', 'PURCHASE_ORDER_QUALITATIVE_RECEPTION', 'TASK']
    if ((!resourceType && !permissionResource) || (!permissionEnabledResourceTypes.includes(resourceType) && !permissionEnabledResourceTypes.includes(permissionResource))) {
      return true
    }
    const permissionName = 'DOCUMENTS_' + (permissionResource !== undefined ? permissionResource : resourceType) + '_' + action
    return isUserAuthorized(permissionName)
  }, [permissionResource, resourceType, isUserAuthorized])

  const [documentTypes, setDocumentTypes] = useState([])
  const [maxFileSize, setMaxFileSize] = useState(undefined)
  const [creatingNewRow, setCreatingNewRow] = useState(null)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const [isUploadingDroppedFiles, setIsUploadingDroppedFiles] = useState(false)
  const [reloadDocuments, toggleReloadDocuments] = useToggle()
  // Document id currently flagged as the reference main image (empty string when none).
  const [mainImageDocumentId, setMainImageDocumentId] = useState('')

  // The main-image action is only offered for resource types that support it (PART_REFERENCE,
  // DEVICE, … see mainImageResource) AND when the user has permission to edit that resource (S38).
  // Visualization permissions are irrelevant here.
  const mainImageConfig = mainImageResource(resourceType)
  const mainImageEditPermission = mainImageConfig?.editPermission || ''
  const mainImageBatchEndpoint = mainImageConfig?.batchEndpoint || ''
  const canManageMainImage = mainImageEditPermission !== '' &&
    !referenceTempUUID && !viewMode && isUserAuthorized(mainImageEditPermission)

  useEffect(() => {
    if (mainImageEditPermission === '' || referenceTempUUID || reference?.id === undefined || reference?.id === null) {
      return
    }
    let isCurrent = true
    const fetchMainImage = async () => {
      try {
        const map = await apiPost(mainImageBatchEndpoint, [reference.id])
        const info = (map && map[reference.id]) || {}
        if (isCurrent) {
          setMainImageDocumentId(info.hasMainImage && info.documentId !== undefined && info.documentId !== null ? info.documentId : '')
        }
      } catch (error) {
        if (isCurrent) {
          setMainImageDocumentId('')
        }
      }
    }
    fetchMainImage()
    return () => {
      isCurrent = false
    }
  }, [mainImageEditPermission, mainImageBatchEndpoint, referenceTempUUID, reference])

  const markAsMainImage = async (row) => {
    // Guard against formats that Sun accepts to preview but cannot use as a main image (e.g. webp has
    // no server-side thumbnail, svg is rejected): tell the user exactly which types are allowed.
    if (!isAllowedMainImage(row?.extension)) {
      showAlert({
        title: '',
        message: t_('This file type cannot be used as a main image. Allowed types: {{types}}', {
          types: mainImageAllowedExtensions.map((extension) => extension.toUpperCase()).join(', ')
        }),
        severity: 'error'
      })
      return
    }
    try {
      await apiPut(mainImageConfig.base(reference.id) + '/' + row.uuid, {})
      setMainImageDocumentId(row.id)
      // Let owners (e.g. the monitoring screen) re-read the main image so a hero/thumbnail
      // already on screen refreshes immediately, even when it is REPLACING an existing one.
      onMainImageChange()
    } catch (error) {
      showAlert({ title: '', message: t_('Could not set the main image'), severity: 'error' })
    }
  }

  const removeMainImage = async () => {
    try {
      await apiDelete(mainImageConfig.base(reference.id), {})
      setMainImageDocumentId('')
      onMainImageChange()
    } catch (error) {
      showAlert({ title: '', message: t_('Could not remove the main image'), severity: 'error' })
    }
  }

  // Toggle a document's main-image mark: unmark if it is already the main image, otherwise mark it.
  // Shared by the inline star in the table row and the star in the image carousel.
  const toggleMainImage = (document) => {
    if (!isPreviewableImage(document?.extension)) {
      return
    }
    if (mainImageDocumentId !== '' && document?.id === mainImageDocumentId) {
      removeMainImage()
      return
    }
    markAsMainImage(document)
  }

  useEffect(() => {
    const fetchData = async () => {
      const types = await apiGet('documentTypes')
      setDocumentTypes(types)
      if (maxFileSize === undefined) {
        const maxSize = await apiGet('settings/MAX_FILE_SIZE')
        let size = maxSize.value
        if (size === null || size === undefined) {
          size = 1024 * 1024 * 1024
          setMaxFileSize(size)
        } else {
          const sizeInBytes = parseInt(size) * 1024 * 1024
          setMaxFileSize(sizeInBytes)
        }
      }
    }
    fetchData()
  }, [maxFileSize])

  const handleFileSelected = (row, file) => {
    if (!file) return
    if (maxFileSize !== undefined && file.size > maxFileSize) {
      showAlert({ title: '', message: t_('File size exceeds the maximum limit'), severity: 'error' })
      return
    }
    if (file.name && file.name.length > 80) {
      showAlert({ title: '', message: t_('File name cannot exceed 80 characters'), severity: 'error' })
      return
    }
    const ext = file.name.split('.').pop()
    row.__file = file
    row.fileName = file.name
    row.extension = ext
  }

  const getNextOrder = async () => {
    if (reference === null || reference.id === null) {
      return 1
    }
    let url = 'document/getNextOrder/' + resourceType + '/' + reference.id
    if (referenceTempUUID) {
      if (reference.id) {
        url = 'document/getNextOrder/' + resourceType + '/' + reference.id + '/' + referenceTempUUID
      } else {
        url = 'documents/getNextOrder/' + resourceType + '/' + referenceTempUUID
      }
    }
    const data = await apiGet(url)
    return parseInt(data.message)
  }

  // Dropping files on the window creates one document per file, with no row-by-row typing: same
  // behaviour as the task instruction documents header (S9/S10/S11), reusing its pure helpers.
  const canDropFiles = enableFileDrop && !viewMode && isUserAuthorizedForAction('ADD') && !isDownloadingFile

  const uploadDroppedFiles = async (files) => {
    const { accepted, rejected } = partitionUploadFiles(files, maxFileSize)
    if (rejected.length > 0) {
      showAlert({
        title: '',
        severity: 'warning',
        message: t_('The following files could not be uploaded: {{files}}', {
          files: rejected.map((entry) => entry.file.name).join(', ')
        })
      })
    }
    const defaultDocumentType = documentTypes.find((documentType) => documentType?.name?.toLowerCase() === 'general')
    if (accepted.length === 0 || !defaultDocumentType) {
      return
    }
    setIsUploadingDroppedFiles(true)
    try {
      const createUrl = buildDocumentCreateUrl({ resourceType, referenceId: reference?.id, referenceTempUUID, referenceUUID })
      const payloads = buildAppendDocumentPayloads(accepted, await getNextOrder(), defaultDocumentType)
      // Sequentially: a temp document keeps the order sent by the client, and Sun's temp document
      // store is a plain in-memory list, so the batch is appended one file at a time.
      for (const payload of payloads) {
        await apiFile(createUrl, payload.body, payload.file)
      }
      toggleReloadDocuments()
    } catch (error) {
      showAlert({ title: '', message: t_('Error creating document'), severity: 'error' })
    } finally {
      setIsUploadingDroppedFiles(false)
    }
  }

  const handleFilesDrop = (event) => {
    event.preventDefault()
    setIsDraggingFiles(false)
    if (!canDropFiles || isUploadingDroppedFiles) {
      return
    }
    uploadDroppedFiles(Array.from(event.dataTransfer?.files || []))
  }

  const checkIsNumeric = (row) => {
    let errorMsg = ''
    if (isNaN(row.documentOrder)) {
      errorMsg = t_('Order must be a number')
      return errorMsg
    }
    if (!Number.isInteger(parseFloat(row.documentOrder))) {
      errorMsg = t_('Order must be an integer')
      return errorMsg
    }
    return errorMsg
  }

  const columns = [
    {
      name: t_('Order'),
      field: 'documentOrder',
      mandatory: true,
      unique: true,
      defaultNewValue: async () => await getNextOrder(),
      condition: (rowData) => {
        return checkIsNumeric(rowData)
      }
    },
    {
      name: t_('Document name'),
      field: 'documentName',
      charLimit: 80,
      mandatory: true
    },
    {
      name: t_('File name'),
      field: 'fileName',
      charLimit: 80,
      mandatory: true,
      readOnly: (row) => Boolean(row?.id),
      type: 'file',
      edit: {
        onFileSelected: (row, file) => handleFileSelected(row, file)
      }
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Type'),
      defaultNewValue: documentTypes?.find(
        dt => dt?.name?.toLowerCase() === 'general'),
      field: 'documentType.name',
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('documentTypes')
        },
        id: 'id',
        field: 'name'
      },
      mandatory: true
    },
    {
      name: t_('Create date'),
      field: 'createdDate',
      type: 'dateTimePicker',
      readOnly: true,
      hide: creatingNewRow
    },
    {
      name: t_('Created user'),
      field: 'createdUser',
      readOnly: true,
      hide: creatingNewRow
    },
    {
      name: t_('Create node'),
      field: 'createdNode',
      readOnly: true,
      hide: creatingNewRow
    },
    {
      name: t_('Last update date'),
      field: 'updatedDate',
      type: 'dateTimePicker',
      readOnly: true,
      hide: creatingNewRow
    },
    {
      name: t_('Last updated user'),
      field: 'updatedUser',
      readOnly: true,
      hide: creatingNewRow
    }

  ]

  // if (resourceType === 'JOB') {
  //   columns.push(...columnAssignedIn)
  // }

  const downloadFile = async (row) => {
    setDownloadingRowId(row.id)
    setIsDownloadingFile(true)

    try {
      let url = 'document/file/download/' + row.uuid
      if (referenceTempUUID && !row.id) {
        url = 'document/file/download/' + row.uuid + '/' + referenceTempUUID
      }
      await apiDownloadFile(url, row.fileName)
    } catch (error) {
      console.error('Error downloading file:', error)
      showAlert({
        title: t_('Download Error'),
        message: t_('An error occurred while downloading the file.'),
        severity: 'error'
      })
    } finally {
      setDownloadingRowId(null)
      setIsDownloadingFile(false)
    }
  }

  const actions = {
    create: {
      fetchFunction: (row) => {
        if (!row.__file) {
          showAlert({ title: '', message: t_('Select a file'), severity: 'warning' })
          return Promise.reject(new Error('FILE_REQUIRED'))
        }
        const nameVal = row?.documentType?.name
        const typeObj = typeof nameVal === 'string'
          ? documentTypes.find(dt => dt.name === nameVal) || null
          : row.documentType || null
        const payload = {
          documentName: row.documentName || '',
          description: row.description || '',
          documentType: typeObj,
          order: row.documentOrder
        }

        let url = 'document/create/' + resourceType + '/' + reference.id

        if (referenceTempUUID) {
          url = 'document/createTemp/' + resourceType + '/' + referenceTempUUID + '/' + referenceUUID
        }
        return apiFile(url, payload, row.__file)
          .then((res) => {
            row.__file = null
            if (row.__fileInputEl) {
              row.__fileInputEl.value = ''
            }
            row.fileName = ''
            row.extension = ''
            return res
          })
      },
      disabled: isDownloadingFile || !isUserAuthorizedForAction('ADD') || viewMode
    },
    edit: {
      cancelSorting: true,
      fetchFunction: async (row) => {
        // Story 213: the mark (`sensitive*`, read-only, added by Sun to the row) is not part of the document.
        const { sensitive, sensitiveInherited, sensitiveVia, ...document } = row
        return referenceTempUUID
          ? apiPut('document/updateTemp/' + referenceTempUUID + '/' + referenceUUID, document)
          : apiPut('document/update', document)
      },
      disabled: isDownloadingFile || !isUserAuthorizedForAction('EDIT') || viewMode
    },
    delete: {
      fetchFunction: (row) => {
        if (referenceTempUUID) {
          return apiDelete('document/deleteTemp/' + resourceType + '/' + referenceTempUUID + '/' + referenceUUID + '/' + row[0].uuid)
        }
        return apiDelete('document/delete/' + resourceType + '/' + reference.id + '/' + row[0].id)
      },
      disabled: isDownloadingFile || !isUserAuthorizedForAction('DELETE') || viewMode
    },
    resync: {
      resource: 'DOCUMENT',
      disabled: !isUserAuthorizedForAction('RESYNC'),
      visibilityButton: () => { return frontEndConfig.inCluster },
      documentType: resourceType,
      documentResourceId: reference.id
    },
    custom: [
      {
        name: t_('View file'),
        render: (row) => {
          return (
            <IconButton>
              <Visibility />
            </IconButton>
          )
        },
        onClick: (row) => {
          if (!isExtensionViewable(row.extension)) {
            showAlert({
              title: '',
              message: t_('Currently file view not available to the extension file: ') + row.extension,
              severity: 'warning'
            })
            return
          }
          setSelectedDocument(row)
          setOpenDocumentDialog(true)
        },
        disabled: isDownloadingFile || !isUserAuthorizedForAction('VIEW')
      },
      {
        name: t_('Download file'),
        render: (row) => {
          if (downloadingRowId === row.id && isDownloadingFile) {
            return (
              <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </div>
            )
          }
          return (
            <IconButton>
              <FileDownloadOutlined />
            </IconButton>
          )
        },
        onClick: (row) => downloadFile(row),
        disabled: isDownloadingFile || !isUserAuthorizedForAction('DOWNLOAD')
      }
    ]
  }

  if (canManageMainImage) {
    actions.custom.push({
      name: t_('Set as main image'),
      // Surface the star inline (to the left of the row's options menu) instead of buried inside it,
      // so it is always visible per row without opening the kebab. `contextMenu: false` makes
      // SmartMateriaUITable render `render(row)` directly in the row action stack; that inline path
      // does not wire `onClick`, so the star's IconButton carries its own onClick.
      contextMenu: false,
      // Only formats that can actually be marked as main image (isAllowedMainImage: jpg/jpeg/png/
      // gif/bmp; webp/svg excluded) get the star, so it is never offered where marking would fail
      // (S38). The whole action is also gated by the reference-edit permission (canManageMainImage).
      hide: (row) => !isAllowedMainImage(row?.extension),
      render: (row) => {
        if (!isAllowedMainImage(row?.extension)) {
          // Not markable: render an empty slot the same size as the star instead of nothing, so the
          // row's options menu (kebab) stays in the same position on every row — otherwise the
          // centered action stack shifts the kebab to the middle on rows without a star.
          return <Box aria-hidden sx={{ width: 40, height: 40 }} />
        }
        const isMain = mainImageDocumentId !== '' && row?.id === mainImageDocumentId
        const label = isMain ? t_('Remove main image') : t_('Set as main image')
        return (
          <Tooltip title={label}>
            {/* span wrapper so the tooltip still shows while the button is disabled */}
            <span>
              <IconButton
                aria-label={label}
                disabled={isDownloadingFile}
                onClick={() => toggleMainImage(row)}
              >
                {isMain ? <Star color='warning' /> : <StarBorder />}
              </IconButton>
            </span>
          </Tooltip>
        )
      },
      onClick: toggleMainImage,
      disabled: isDownloadingFile
    })
  }

  const dataFetch = async () => {
    if (reference === null || reference.id === null) {
      return []
    }
    let url = 'document/getAll/' + resourceType + '/' + reference.id
    if (referenceTempUUID) {
      if (reference.id) {
        url = 'document/getAll/' + resourceType + '/' + reference.id + '/' + referenceTempUUID
      } else {
        url = 'documents/getAll/' + resourceType + '/' + referenceTempUUID
      }
    }
    const documents = await apiGet(url)
    documentListRef.current = documents
    return documents
  }
  return (
    <div>
      {AlertElement}
      <SmartDialog
        setOpen={openDocumentDialog}
        accept={false}
        cancel={false}
        close
        closeCallback={() => {
          setOpenDocumentDialog(false)
        }}
        minWidth='60vw'
        paperHeight='75vh'
        renderComponent={
          <DocumentsCarouselView
            resourceType={resourceType}
            referenceUUID={reference?.id}
            referenceTempUUID={referenceTempUUID}
            referenceId={reference?.id}
            initialDocumentUUID={selectedDocument?.uuid}
            canManageMainImage={canManageMainImage}
            mainImageDocumentId={mainImageDocumentId}
            onToggleMainImage={toggleMainImage}
          />
        }
        growContainerHeight
      />
      <Box
        data-testid={canDropFiles ? 'documents-table-dropzone' : undefined}
        onDragOver={canDropFiles ? (event) => { event.preventDefault(); setIsDraggingFiles(true) } : undefined}
        onDragLeave={canDropFiles ? () => setIsDraggingFiles(false) : undefined}
        onDrop={canDropFiles ? handleFilesDrop : undefined}
        sx={{
          position: 'relative',
          borderRadius: 1,
          // The dashed frame only shows up while files are hovering over the window, so the drop
          // target is announced exactly when it is usable and never adds noise otherwise.
          outline: isDraggingFiles ? (theme) => '2px dashed ' + theme.palette.primary.main : 'none',
          outlineOffset: '2px'
        }}
      >
        {canDropFiles && (isDraggingFiles || isUploadingDroppedFiles) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              pointerEvents: 'none',
              backgroundColor: (theme) => theme.palette.background.paper + 'CC'
            }}
          >
            {isUploadingDroppedFiles && <CircularProgress size={24} />}
            <Typography variant='h6'>
              {isUploadingDroppedFiles ? t_('Adding documents') : t_('Drop the files to add them as documents')}
            </Typography>
          </Box>
        )}
        <SmartMateriaUITable
          rowPlaceholder={protectedRowPlaceholder}
          disableSelectAllData={visionCoversSensitive}
          tableTuneUniqueKey='documents'
          columns={columns}
          title={t_('Documents') + ' - ' + (defaultName || reference?.name)}
          dataFetch={dataFetch}
          actions={actions}
          forceLoad={reloadDocuments}
          validator={(row) => validateDocumentRow(row, documentListRef.current, t_)}
          onEditIndexChanged={(index) => setCreatingNewRow(index === -1)}
        />
      </Box>
    </div>
  )
}

export default Document
